<template>
  <div class="flex flex-col gap-1 mb-8">
    <ol>
      <li class="flex items-center gap-1">
        1. Je crée un nouveau parapheur depuis la page d'accueil
        <PButton size="sm" class="ml-1">
          <OhVueIcon :name="activatorIcon" class="mr-1" />
          Créer un parapheur
        </PButton>
      </li>
      <li class="flex items-center gap-1">
        2. Je donne un nom à mon parapheur en cliquant sur
        <PButton size="icon" mode="tertiary-borderless" aria-label="Saisir le nom du parapheur">
          <OhVueIcon :name="editIcon" scale="1.5" />
        </PButton>
      </li>
      <li class="flex items-center gap-1">
        3. J'ajoute un ou plusieurs destinataires au flux de mon parapheur
        <PButton mode="secondary" size="sm"> Ajouter une autre étape</PButton>
      </li>
      <li class="flex items-center gap-1">
        4. J'envoie le parapheur au destinataire suivant
        <PButton size="sm">Passer ce parapheur à l'étape suivante</PButton>
      </li>
    </ol>
    <PAccordion
      label="Gérer les documents de votre parapheur"
      label-tag="h2"
      class="py-1 [&>button]:border-grey-625 border-b-grey-625"
    >
      <p>Je peux déposer mon ou mes documents directement dans la zone dédiée (drag and drop)</p>
      <p>Je peux ajouter un lien internet</p>
      <p class="flex items-center gap-1">
        J'accède aux options
        <PButton size="icon" mode="tertiary-borderless">
          <OhVueIcon :name="menuIcon" :scale="1" class="text-blue-france-sun-113" />
        </PButton>
      </p>
      <ul>
        <li class="flex items-center gap-1">
          <OhVueIcon :name="checkIcon" class="text-green-bourgeon-850 mr-1" />
          Ouvrir (permet de visualiser le document ou de le signer)
        </li>
        <li v-if="wopiStore.k2000Enabled" class="flex items-center gap-1">
          <OhVueIcon :name="checkIcon" class="text-green-bourgeon-850 mr-1" />
          Modifier sur le poste
        </li>
        <li v-if="wopiStore.enabled" class="flex items-center gap-1">
          <OhVueIcon :name="checkIcon" class="text-green-bourgeon-850 mr-1" />
          Modifier en ligne
        </li>
        <li class="flex items-center gap-1">
          <OhVueIcon :name="checkIcon" class="text-green-bourgeon-850 mr-1" />
          Télécharger
        </li>
        <li class="flex items-center gap-1">
          <OhVueIcon :name="checkIcon" class="text-green-bourgeon-850 mr-1" />
          Envoyer par mail
        </li>
        <li class="flex items-center gap-1">
          <OhVueIcon :name="checkIcon" class="text-green-bourgeon-850 mr-1" />
          Remplacer par
        </li>
        <li class="flex items-center gap-1">
          <OhVueIcon :name="checkIcon" class="text-green-bourgeon-850 mr-1" />
          Voir les versions
        </li>
        <li class="flex items-center gap-1">
          <OhVueIcon :name="checkIcon" class="text-green-bourgeon-850 mr-1" />
          Supprimer le document
        </li>
      </ul>

      <p class="mt-1">
        Certaines options peuvent être grisées, lorsque l'action n'est pas autorisée. Exemple : "Signer" si le parapheur
        qui contient le document a été désarchivé.
      </p>
      <p>
        Certaines options peuvent être absentes, lorsque l'action est impossible. Exemple : "Modifier" si le document un
        fichier pdf.
      </p>
    </PAccordion>
    <PAccordion
      v-if="wopiStore.k2000Enabled"
      label="Modifier un document sur le poste et revenir dans le Parapheur"
      label-tag="h2"
      class="py-1 [&>button]:border-grey-625 border-b-grey-625"
    >
      <div class="mb-2">
        <p>
          Je peux modifier les documents bureautiques sur le poste, avec Word (Microsoft Office) ou Writer (LibreOffice
          ou Collabora Desktop).
        </p>
        <p>Les modifications sont enregistrées dans le Parapheur.</p>
      </div>
    </PAccordion>

    <PAccordion
      v-if="wopiStore.enabled"
      label="Modifier un document en ligne et revenir dans le Parapheur"
      label-tag="h2"
      class="py-1 [&>button]:border-grey-625 border-b-grey-625"
    >
      <div class="mb-2">
        <p>Je peux modifier les documents bureautiques dans Collabora, l'éditeur en ligne intégré au Parapheur.</p>
        <p>Les modifications sont enregistrées au fil de l'eau.</p>
      </div>
    </PAccordion>

    <PAccordion
      label="Création rapide d'un parapheur depuis un mél"
      label-tag="h2"
      class="py-1 [&>button]:border-grey-625 border-b-grey-625"
    >
      <div class="mb-2">
        <p>
          Vous pouvez initialiser un parapheur en envoyant un mail à parapheur@developpement-durable.gouv.fr. L'objet du
          mail, son corps et ses pièces jointes sont repris dans le parapheur créé.
        </p>
        <ul>
          <li class="flex items-center">
            <OhVueIcon :name="dotIcon" :scale="1.5" class="self-start" />
            Tous les destinataires en copie du message sont ajoutés en copie du parapheur créé. Ils recevront une
            notification par mail lors de la création du parapheur, selon les règles qu'ils auront paramétrées dans
            leurs profils respectifs.
          </li>
          <li class="flex items-center">
            <OhVueIcon :name="dotIcon" :scale="1.5" class="self-start" />
            Tous les destinataires directs sont ajoutés dans le flux du parapheur créé. Ils recevront une notification
            lorsque le parapheur arrivera à leur niveau, selon les règles qu'ils auront paramétrées dans leurs profils
            respectifs.
          </li>
        </ul>
      </div>
    </PAccordion>
    <PAccordion label="Flux" label-tag="h2" class="py-1 [&>button]:border-grey-625 border-b-grey-625">
      <p class="flex items-center gap-1">
        Avec
        <PButton size="icon" mode="tertiary-borderless">
          <OhVueIcon :name="menuIcon" :scale="1" class="text-blue-france-sun-113" />
        </PButton>
        je peux enregistrer le flux du parapheur ou utiliser un flux déjà enregistré
      </p>
      <p class="flex items-center gap-1">
        Je modifie l'ordre du flux avec
        <OhVueIcon :name="handleIcon" :scale="1" class="text-blue-france-sun-113" />
      </p>
      <ul class="list-disc pl-6">
        <li>
          <div class="flex items-center gap-1">
            J'ajoute rapidement dans le flux avec
            <PTooltip label="Ajouter en bas du flux" :delay="500" :offset="5">
              <PButton size="icon" mode="tertiary-borderless" aria-label="Ajouter en bas du flux">
                <OhVueIcon :name="addUserIcon" class="text-success" />
              </PButton>
            </PTooltip>
          </div>
        </li>
        <li>
          <div class="flex items-center gap-1">
            Je supprime du flux définitivement ou je mets en copie avec
            <PTooltip label="Supprimer du flux" :delay="500">
              <PButton
                size="icon"
                mode="tertiary-borderless"
                class="delete-btn"
                aria-label="Supprimer l'étape"
                color="error"
              >
                <OhVueIcon :name="deleteUserIcon" />
              </PButton>
            </PTooltip>
          </div>
        </li>
      </ul>
    </PAccordion>
    <PAccordion label="Options avancées" label-tag="h2" class="py-1 [&>button]:border-grey-625 border-b-grey-625">
      <p class="flex items-center gap-1 flex-wrap">
        Je peux ajouter un badge pour signaler le parapheur
        <PSelectableTag v-model="select"> Urgent</PSelectableTag>
        Si le badge n'apparait pas à l'écran, vous devez cliquer sur
        <PTooltip :offset="5" :delay="500" label="Ajouter un nouveau badge">
          <PButton mode="secondary" class="w-full"> Ajouter</PButton>
        </PTooltip>
        pour le sélectionner ou en demander la création, s'il n'existe pas encore.
      </p>
      <p>Je peux ajouter une date limite de traitement</p>
      <p>Je peux ajouter des informations nécessaires au traitement du parapheur</p>
    </PAccordion>
    <PAccordion label="Commentaires" label-tag="h2" class="py-1 [&>button]:border-grey-625 border-b-grey-625">
      <p class="flex items-center gap-1">Je peux saisir un commentaire et je l'envoie</p>
      <p class="flex items-center gap-1">
        Je peux saisir un commentaire privé ou enregistrer un commentaire audio en cliquant sur
        <PButton size="icon" mode="tertiary-borderless" aria-label="Enregistrer un message vocal privé">
          <OhVueIcon :name="micIcon" />
        </PButton>
        , via
        <span class="bg-red-marianne-850 p-1v">
          <PToggleSwitch label="Privé" label-position="left" :show-state="false" v-model="togglePrivate" />
        </span>
        et je l'envoie
      </p>

      <p>
        <strong class="font-bold uppercase">Important</strong> les commentaires ne doivent pas être inappropriés,
        subjectifs et insultants.
      </p>
    </PAccordion>
    <PAccordion
      label="Mettre en copie sur un parapheur"
      label-tag="h2"
      class="py-1 [&>button]:border-grey-625 border-b-grey-625"
    >
      <p class="flex items-center">
        Je peux ajouter un destinataire (agent ou boite) ou un flux enregistré via
        <PButton
          size="icon"
          mode="tertiary-borderless"
          class="grow-0 mr-1.5"
          aria-label="Ajouter une personne en copie"
        >
          <OhVueIcon :name="addToCCIcon" scale="1.2" />
        </PButton>
        , pour information sur un parapheur
      </p>
      <p>
        Pour cela, il suffit de saisir le nom du destinataire ou du flux enregistré dans la zone de saisie qui se trouve
        dans la fenêtre qui s'affiche.
      </p>
      <p>
        Tous les agents qui ont accès au parapheur, notamment lorsqu'ils sont en copie, sont indiqués sur cette fenêtre.
      </p>
      <p class="flex items-center gap-1">
        Lorsque je supprime un destinataire (agent ou boite) du flux via
        <PTooltip label="Supprimer du flux" :delay="500">
          <PButton
            size="icon"
            mode="tertiary-borderless"
            class="delete-btn"
            aria-label="Supprimer l'étape"
            color="error"
          >
            <OhVueIcon :name="deleteUserIcon" />
          </PButton>
        </PTooltip>
        , je peux choisir de l’ajouter en copie. Pour cela, je ne décoche pas
        <PCheckbox v-model="addToCC" label="Ajouter l'utilisateur en copie" hide-messages />
      </p>
      <p>Un destinataire ne peut être ajouté en copie sur un parapheur qu’une seule fois</p>
      <p>
        Il est possible de supprimer un agent en copie tant que le parapheur n'est pas passé à l'étape suivante. Pour
        cela, il suffit de cliquer sur
        <button class="mx-1">
          <OhVueIcon :name="deleteIcon" scale="1" class="text-blue-france-sun-113 cursor-pointer" />
        </button>
        situé à droite du nom.
      </p>
    </PAccordion>
    <PAccordion label="Dupliquer un parapheur" label-tag="h2" class="py-1 [&>button]:border-grey-625 border-b-grey-625">
      <p class="mb-1">
        Un parapheur peut être dupliqué soit niveau de l'écran d'accueil soit directement dans l'écran de détail du
        parapheur.
      </p>
      <PButton mode="secondary" class="mb-1"> Dupliquer</PButton>
      <p>
        Il est possible de sélectionner les informations à dupliquer parmi ses documents, son flux, ses métadonnées et
        les personnes en copie. Seuls les commentaires ne peuvent pas être dupliqués.
      </p>
    </PAccordion>
    <PAccordion label="Supprimer un parapheur" label-tag="h2" class="py-1 [&>button]:border-grey-625 border-b-grey-625">
      <p class="mb-1">Un parapheur qui ne comporte aucun document signé peut être supprimé par son initiateur.</p>
      <PButton color="error" mode="secondary" class="mb-1"> Je supprime le parapheur</PButton>
      <p>La suppression des données et des documents du parapheur est définitive.</p>
      <p>Seuls les parapheurs qui ne comportent aucun document signé peuvent être supprimés.</p>
    </PAccordion>
    <PAccordion label="Alerte par mél" label-tag="h2" class="py-1 [&>button]:border-grey-625 border-b-grey-625">
      <p>Il est possible d'inviter un utilisateur présent dans le flux à revoir le parapheur.</p>

      <p>
        Pour cela, je passe par
        <PTooltip class="ml-1v inline-block" label="Inviter à revoir le parapheur" :delay="500" :offset="5">
          <PButton size="icon" mode="tertiary-borderless" class="mr-1v">
            <OhVueIcon :name="bellIcon" class="text-blue-france-sun-113" />
          </PButton>
        </PTooltip>
      </p>

      <p>L'utilisateur choisi recevra une notification.</p>
      <p>Et, s'il est absent, je recevrai aussi une notification.</p>
    </PAccordion>
  </div>
</template>

<script setup lang="ts">
import { PAccordion, PButton, PCheckbox, PSelectableTag, PToggleSwitch, PTooltip, useIcons } from "@pasta/box-v2";
import { OhVueIcon } from "oh-vue-icons";
import {
  BiDot,
  MdDraghandle,
  OiBell,
  RiAddLine,
  RiCheckboxCircleFill,
  RiDeleteBin6Line,
  RiEditFill,
  RiMicLine,
  RiMore2Fill,
  RiUserAddFill,
  RiUserAddLine,
  RiUserUnfollowFill,
} from "oh-vue-icons/icons";
import { ref } from "vue";
import { useWopiStore } from "../../stores/wopi.store.ts";

const wopiStore = useWopiStore();

const [
  activatorIcon,
  editIcon,
  menuIcon,
  addToCCIcon,
  checkIcon,
  handleIcon,
  micIcon,
  bellIcon,
  deleteUserIcon,
  addUserIcon,
  deleteIcon,
  dotIcon,
] = useIcons(
  RiAddLine,
  RiEditFill,
  RiMore2Fill,
  RiUserAddLine,
  RiCheckboxCircleFill,
  MdDraghandle,
  RiMicLine,
  OiBell,
  RiUserUnfollowFill,
  RiUserAddFill,
  RiDeleteBin6Line,
  BiDot
);

const select = ref<boolean>(false);
const togglePrivate = ref<boolean>(true);
const addToCC = ref<boolean>(true);
</script>
